/** Manages RMA list data fetching with dual-backend support (Laravel proxy / rms-api).
 *  Before routing, loads BETA_RMS_API_MANAGE_RETURNS via CurrentPortalStore so the flag is
 *  not read before user features are available. When enabled, all three endpoints
 *  (list, filters, list-by-ids) use rms-api via RmsApiConnectionManager; otherwise the
 *  Laravel proxy. Optional `include_tab_counts` (rms-api only) requests Open/Closed/All
 *  aggregate counts on the list response.
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import { IFetchListArgs, IPagedData } from "../../stores/abstract-paged-data-store";
import { createPagedRequestURLSearchParams } from "../helpers";
import { RMA_STATUS } from "../../pages/manage-returns/rma-page/rma-types";
import { IResolutionCategoryPreset } from "../../pages/policies/policy-types";
import RmsApiConnectionManager from "../rms-api-connection-manager";
import currentPortalStore from "../../stores/current-portal-store";
import { FEATURES } from "../../models/Plan";

/** This is not one to one Rma model's status, it computed on server and it considers shipment status too */
export enum RMA_STATUS_TRANSLATION_KEY {
    PENDING = 'PENDING',
    DONE = 'DONE',
    APPROVED = 'APPROVED',
    RECEIVED = 'RECEIVED',
    REJECTED = 'REJECTED',
    CANCELED = 'CANCELED',
}

export enum RMA_PROVIDERS {
    REFUND_ID = 'refundid',
    REVENI = 'reveni',
    RISE = 'rise',
}

export enum RESTOCK_VALIDATED_TYPE {
    ALL = 'all',
    VALIDATED = 'filter_restock_is_validated',
    NOT_VALIDATED = 'filter_restock_is_not_validated',
    PARTIALLY_VALIDATED = 'filter_restock_is_partially_validated'
}

export enum PAYMENT_STATUS_TYPE {
    ALL = 'all',
    NOT_REFUNDED = 'not_refunded',
    REFUNDED = 'refunded',
    NOT_PAID = 'not_paid',
    PAID = 'paid',
    EXPIRING = 'expiring',
}

export enum RESERVED_ITEM_EXPIRATION_TYPE {
    ALL = 'all',
    DATE_RANGE = 'date_range',
    DAYS_REMAINING = 'days_remaining',
    EXPIRED = 'expired',
}

/**
 * Manual-action bucket types for the "Actions status" filter group. Values mirror the
 * rms-api `manual_actions` contract; the API only returns the ones visible for the store's settings,
 * so the client renders whatever it receives rather than hard-coding a visible subset.
 */
export enum MANUAL_ACTION_TYPE {
    APPROVE = 'approve',
    MANAGER_APPROVAL = 'manager_approval',
    RECEIVE = 'receive',
    REFUND = 'refund',
    RELEASE_EXCHANGE = 'release_exchange',
    VALIDATE = 'validate',
    RESTOCK = 'restock',
    MARK_DONE = 'mark_done',
}


export enum IRmaListTab {
    STATUS_GROUP_CLOSED = 'CLOSED',
    STATUS_GROUP_OPEN = 'OPEN',
    STATUS_GROUP_ALL = 'ALL',
}
export interface IRmaListItem {
    id: number;
    order_name: string;
    _customer_name: string;
    amount: number;
    is_gift: boolean;
    is_out_of_policy: boolean;
    last_unresolved_error: string | null;
    status: RMA_STATUS;
    reserve_item_count: number;
    _release_date: string | null;
    is_returngo_resolution: boolean;
    has_exchange: boolean;
    no_offers: number | null;
    date_to_show: string;
    _order_url: string,
    _customer_url: string,
    _status: RMA_STATUS_TRANSLATION_KEY,
    _images_urls: string[],
    _reserve_until_date: string | null,
    _item_reservation_timeframe: number,
    _is_instant_exchange: boolean,
    _exchange_draft_order_id: number | null,
    _resolution_name_key: string,
    _resolution_internal_name: string,
    _has_multiple_resolutions: boolean,
    _refund_provider: RMA_PROVIDERS | null,
    _credit_integration_provider: RMA_PROVIDERS | null,
}

export interface IFetchRmaListArgs extends IFetchListArgs {
    search_input: string;
    search_exact_match: boolean;
    filter_resolution_id: number | null;
    filter_return_reason_id: number | null;
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE;
    filter_date_range: string | null;
    filter_status: string | null;
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE;
    filter_reserve_item_expiration_input: string;
    filter_payment_status_type: PAYMENT_STATUS_TYPE;
    /**
     * Selected manual-action buckets. Sent OR-combined as a comma-separated `manual_actions`
     * query param; omitted when empty. Only used by the redesigned page's Open tab.
     */
    filter_manual_actions?: string[];
    /**
     * Selected error-bucket labels. Sent OR-combined as REPEATED `error_labels` query params
     * (not comma-joined — labels are free text and may contain commas); omitted when empty. Redesigned
     * page's Open tab only.
     */
    filter_error_labels?: string[];
    /**
     * Optional: omitted on the legacy page (which has no tab UI). When sent, it must be paired
     * with a compatible `filter_status` -- the rms-api enforces this as a 400 cross-field check.
     */
    status_group?: IRmaListTab;
    /**
     * Opt-in flag for the `tab_counts` block on the paginated response. Sent by the redesigned
     * page's paged store on every request (the page always renders Open/Closed/All counts);
     * the legacy page never sends it.
     */
    include_tab_counts?: boolean;
}

export interface IRmaTabCounts {
    open: number;
    closed: number;
    all: number;
}

/**
 * Paginated GET /manage-returns (and Laravel proxy) response envelope.
 * `tab_counts` is optional metadata on the list response (sibling of `data`, `total`, etc.),
 * present only when the request sends `include_tab_counts=true`. Not a field on `IRmaListItem`.
 */
export interface IRmaListPagedData extends IPagedData<IRmaListItem> {
    tab_counts?: IRmaTabCounts;
}

export interface IReturnReason {
    id: number,
    name: string,
}

export interface IManualActionOption {
    value: string,
    count: number,
}

export interface IManualActions {
    total: number,
    options: IManualActionOption[],
}

/** One error-bucket sub-row: a merchant-facing error label and the count of open RMAs carrying it. */
export interface IErrorBucketOption {
    value: string,
    count: number,
}

/**
 * The `errors` bucket: open RMAs with unresolved errors aggregated by error label, plus the
 * distinct grand total. Independent of manual actions / automation tracking.
 */
export interface IErrors {
    total: number,
    options: IErrorBucketOption[],
}

export interface IRmaListFilterOptions {
    resolutions: IResolutionCategoryPreset[],
    return_reasons: IReturnReason[],
    /** Present only when the rms-api automation-tracking read side is active; absent on older backends. */
    manual_actions?: IManualActions,
    /** Error buckets. Present on rms-api; absent on the legacy Laravel proxy. */
    errors?: IErrors,
}

@injectable()
class RmaListService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);
    private readonly rmsApiConnection: RmsApiConnectionManager = rootContainer.get<RmsApiConnectionManager>(TYPE.RmsApiConnectionManager);

    private isRmsApiManageReturnsEnabled(): boolean {
        return currentPortalStore.isFeatureAllowed(FEATURES.BETA_RMS_API_MANAGE_RETURNS);
    }

    /**
     * Fetches a paginated RMA list. Two independent concerns:
     *
     * 1) **Backend routing** (`BETA_RMS_API_MANAGE_RETURNS`):
     *    - enabled  -> rms-api `GET /manage-returns`
     *    - disabled -> Laravel `GET /rms-api/manage-returns/rma-list` (shopify-app proxy)
     *    Remove the Laravel branch when the flag is fully rolled out.
     *
     * 2) **Redesigned UI only** (caller selects the right paged store):
     *    - `status_group` -- Open/Closed/All tab scope (omitted by the legacy paged store to avoid 400)
     *    - `include_tab_counts` -- aggregate counts for tab labels (omitted by the legacy paged store)
     *
     * `tab_counts` in the response is present only when the backend supports it AND
     * `include_tab_counts=true` was sent.
     */
    public async fetchRmaList(args: IFetchRmaListArgs): Promise<IRmaListPagedData> {
        await currentPortalStore.loadFeatures([FEATURES.BETA_RMS_API_MANAGE_RETURNS]);

        const {
            page,
            perPage,
            sortBy,
            sortDir,
            search_exact_match,
            search_input,
            filter_resolution_id,
            filter_return_reason_id,
            filter_restock_validated_type,
            filter_date_range,
            filter_status,
            filter_reserve_item_expiration,
            filter_reserve_item_expiration_input,
            filter_payment_status_type,
            filter_manual_actions,
            filter_error_labels,
            status_group,
            include_tab_counts,
        } = args;
        const searchParams = createPagedRequestURLSearchParams(page, perPage, sortBy, sortDir);
        if (search_input) {
            searchParams.append('search_input', search_input);
        }
        if (search_exact_match) {
            searchParams.append('search_exact_match', '1');
        }
        if (filter_resolution_id) {
            searchParams.append('resolution_id', filter_resolution_id.toString());
        }
        if (filter_return_reason_id) {
            searchParams.append('return_reason_id', filter_return_reason_id.toString());
        }
        if (filter_restock_validated_type) {
            searchParams.append('restock_validated_type', filter_restock_validated_type);
        }
        if (filter_date_range) {
            searchParams.append('date_range', filter_date_range);
        }
        if (filter_status) {
            searchParams.append('status', filter_status);
        }
        if (filter_reserve_item_expiration) {
            searchParams.append('reserve_item_expiration', filter_reserve_item_expiration);
        }
        if (filter_reserve_item_expiration_input) {
            searchParams.append('reserve_item_expiration_input', filter_reserve_item_expiration_input);
        }
        if (filter_payment_status_type) {
            searchParams.append('payment_status_type', filter_payment_status_type);
        }
        if (filter_manual_actions?.length) {
            searchParams.append('manual_actions', filter_manual_actions.join(','));
        }
        if (filter_error_labels?.length) {
            // Repeated param (not comma-joined): error labels are free text and may contain commas.
            filter_error_labels.forEach((label) => searchParams.append('error_labels', label));
        }
        if (status_group) {
            searchParams.append("status_group", status_group);
        }
        if (include_tab_counts === true) {
            searchParams.append("include_tab_counts", "true");
        }

        if (this.isRmsApiManageReturnsEnabled()) {
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListPagedData>>(
                `/manage-returns?${searchParams.toString()}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListPagedData>>(
            `/rms-api/manage-returns/rma-list?${searchParams.toString()}`
        );
        return res.payload;
    }

    public async fetchFilters(): Promise<IRmaListFilterOptions> {
        await currentPortalStore.loadFeatures([FEATURES.BETA_RMS_API_MANAGE_RETURNS]);

        if (this.isRmsApiManageReturnsEnabled()) {
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListFilterOptions>>(
                `/manage-returns/filters`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListFilterOptions>>(`/rms-api/manage-returns/filters`);
        return res.payload;
    }

    public async fetchRmaListByIds(csvIdList: string): Promise<IRmaListItem[]> {
        await currentPortalStore.loadFeatures([FEATURES.BETA_RMS_API_MANAGE_RETURNS]);

        if (this.isRmsApiManageReturnsEnabled()) {
            const res = await this.rmsApiConnection.get<IApiResponse<IRmaListItem[]>>(
                `/manage-returns/rma-list-by-ids?ids=${csvIdList}`
            );
            return res.payload;
        }

        const res = await this.httpService.get<IApiResponse<IRmaListItem[]>>(`/rms-api/manage-returns/rma-list-by-ids?ids=${csvIdList}`);
        return res.payload;
    }

}


export default RmaListService;
